import React, { Fragment } from 'react';
import { Link } from 'react-router';
import { observer } from 'mobx-react';
import { RightOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import type { BreadcrumbSelector, BreadcrumbType } from 'components/breadcrumbs/use-breadcrumbs';
import useBreadcrumbs from 'components/breadcrumbs/use-breadcrumbs';
import { typeStyle } from 'constants/typography';

/**
 * The trail without its data source, so it has a story: `useBreadcrumbs` reads the organization and
 * projects stores over HTTP, and a story must render with no network (frontend/CLAUDE.md).
 */
export const BreadcrumbsBar: React.FunctionComponent<{ breadcrumbs: BreadcrumbType[] }> = ({ breadcrumbs }) => (
  <Wrapper data-e2e="breadcrumbs">
    {breadcrumbs.map((crumb, index) => {
      const isNotLast = index < breadcrumbs.length - 1;
      const Text = isNotLast ? BreadcrumbText : CurrentBreadcrumbText;
      const content = <Text>{crumb.name}</Text>;
      return (
        <Fragment key={index}>
          {!crumb.to ? (
            <Breadcrumb as={InaccessibleLink}>{content}</Breadcrumb>
          ) : (
            <Breadcrumb as={BreadcrumbLink} {...{ to: crumb.to }}>
              {content}
            </Breadcrumb>
          )}
          {isNotLast && (
            <Breadcrumb>
              <Divider />
            </Breadcrumb>
          )}
        </Fragment>
      );
    })}
  </Wrapper>
);

const Crumb: React.FunctionComponent<{ selector?: BreadcrumbSelector }> = ({ selector }) => {
  const breadcrumbs = useBreadcrumbs(selector);

  if (!breadcrumbs) {
    return null;
  }

  return <BreadcrumbsBar breadcrumbs={breadcrumbs} />;
};

/** https://www.figma.com/design/vhfYlbrrUsQrx6AvWGcE9h/Design-system?node-id=151-98 */
const Wrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  min-width: 0;

  ${({ theme }) => css`
    gap: 0 ${theme.spacing.xxs};
  `}
`;

export const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;

  /* No height: the strip in inner-layout.tsx owns Figma's 38px. */
  color: var(--text-body);
`;

export const BreadcrumbText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${typeStyle.labelSm}
`;

/**
 * The trail's last crumb. Figma's component variant binds Text/heading, and both composed usages override
 * the same node to Text/disabled - which is 3.78:1 on the strip and fails AA for 12px text. Shipping the
 * component's value and raising the frames with design (APO-471), the same call `antd.constants` makes for
 * the warning icon. A styled swap, so the DOM and every data-e2e are unchanged.
 */
const CurrentBreadcrumbText = styled(BreadcrumbText)`
  color: var(--text-heading);
`;

const InaccessibleLink = styled.div`
  cursor: not-allowed;
`;

const BreadcrumbLink = styled(Link)`
  color: var(--text-body);

  /*
     * Figma binds Text/action here and resolves it to lime-700, but tokens.css has --text-action as
     * primary-500 - the bright lime, 1.18:1 on this strip - because design-tokens.json predates a Figma
     * edit (APO-471). --text-tint-lime is today's Figma value in both modes. It measures 4.47:1 against
     * surface/main/page, 0.03 under AA for 12px text - the closest thing to Figma's intent that does not
     * invent a colour, and raised with design along with the rest. No underline: the component's
     * description claims one, the text node carries none.
     */
  &:hover {
    color: var(--text-tint-lime);
  }
`;

const Divider = styled(RightOutlined)`
  color: var(--icon-secondary);
  font-size: var(--scale-400);
`;

export default observer(Crumb);
